import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Able, CartItem, OrderInformation, PaymentInfo, ProductSelection, ShippingRateInfo } from '../../app.models';
import { AppSandbox } from '../../app.sandbox';
import { ASSET_BASE_URL } from '../../app.data.service';
import { HttpClient, HttpResponse } from '@angular/common/http';
import to from 'await-to-js';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { calculateShippingRate } from 'src/shipping';
import { calculateETA } from 'src/delivery-estimate';

@Component({
  selector: 'app-shopping-cart',
  templateUrl: './shopping-cart.component.html',
  styleUrls: ['./shopping-cart.component.scss']
})
export class ShoppingCartComponent implements OnInit, OnChanges {
  BASE_MEDIA_URL = ASSET_BASE_URL + '/medium';

  @Input() hideHeader = false;
  @Input() orderInformation: OrderInformation;
  @Input() showComplete = false;
  @Input() processingPayment = false;
  @Input() isEditingPayment = false; // Add this
  @Output() completed = new EventEmitter<{
    cart: CartItem[]
    shippingRate: ShippingRateInfo,
    subtotal: number;
    total: number;
  }>();

  @Input() error: string;
  calculatingShipping = false;
  cart: CartItem[] = [];
  // shippingInfo: ShippingInfo;
  shippingRates: ShippingRateInfo[];
  selectedShippingRate: ShippingRateInfo;
  showRate = true;
  noShippableItems = false;
  salesTax = null;
  salesTaxMessage = null;
  currentRoute = '';

  constructor(private sandbox: AppSandbox, private httpClient: HttpClient, private sanitize: DomSanitizer, private router: Router) { }

  async ngOnInit() {
    this.currentRoute = this.router.url;
    this.sandbox.cart$.subscribe(async (cart) => {
      if (!!cart) {
      //console.log('cart is', cart);
        this.cart = cart;
      } else {
      //console.log('cart is empty');
        this.cart = [];
      }

      await this.calculateAll();
    });
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
  //console.log('have changes', changes);
    if (!!changes && !!changes['showComplete'] && changes['showComplete'].currentValue === true) {
    //console.log('showComplete changed');
      await this.calculateAll();
    }
  }

  sortFields(fields: [{
    name: string;
    type: string;
    order: number;
    required?: boolean;
  }]) {
    // console.log('sortFields', fields);
    const copy = [].concat(fields);

    return copy.sort((a, b) => {
      if (a.order < b.order) {
        return -1;
      } else if (a.order > b.order) {
        return 1;
      }
      return 0;
    });
  }

  async setShippingRate (rate) {
    this.selectedShippingRate = rate;
    console.log(this.selectedShippingRate)
  }

  async calculateShipping() {
    //-----------------------------VARIABLE DECLARATIONS AND FUNCTION SETUP ------------------------//
    // Class boolean flag flips to allow proper caluclation
    this.selectedShippingRate = null;
    this.shippingRates = null;
    this.calculatingShipping = true;
    this.showRate = true;

    const shipDetails = calculateShippingRate(this.cart, this);
    
    const dates = calculateETA();

    // Customer facing shipping rate options. Shipping rate if shipped out, or 0 if picked up at will call. 
    // Customers will be able to choose here. 

    const spaces = 6;
    const indent = '&nbsp;'.repeat(spaces);

    this.shippingRates = [
      {
      name: shipDetails.paidRateAmount == 0
      ? "Free Shipping!"
      : shipDetails.containsGiftCert
        ? `USPS Ground<br>${indent}<em>Includes $4.95 signature confirmation fee</em>`
        : "USPS Ground", 
      desc: `<em>${indent}3-7 business days</em>`,
      amount: shipDetails.paidRate,
      arrives: {
        earliest: dates.etaMinStr,
        latest: dates.etaMaxStr
       }
      },
      {
      name: "Pick up at Farmers Market Customer Service Office",
      desc: `<em>${indent}(Allow 2 business days for processing)</em>`,
      amount: String(0),
      arrives: dates.etaPickupStr,
      instructions:`Order will be ready for pickup on ${dates.etaPickupStr} at the Farmers Market Customer Service Office, located at Gate 1 on the 2nd floor. Pickup hours are from 10 AM to 6 PM. Please have a photo ID ready at the time of pickup. Thank you.<br><br>Please call 323-933-9211 if you need assistance with pick-up. We are happy to make accommodations.`,
      shortInstructions: `Order ready for pickup on ${dates.etaPickupStr} at Farmers Market Customer Service Office (Gate 1, 2nd Floor. Hours: 10AM-6PM. Photo ID required at pickup time. Thank you. Please call 323-933-9211 for assistance or accomodations needed for pickup.`
       
      }
    ];

    this.calculatingShipping = false;
    console.log(`Delivery ETA dates: ${dates.etaMinStr} - ${dates.etaMaxStr}`)
    console.log(`Pickup option date: Available for pickup on or after ${dates.etaPickupStr} after 10 AM`);
    console.log(this.selectedShippingRate)
  }

  async calculateSalesTax() {
    if (!this.orderInformation || !this.showComplete) {
      return;
    }

    const selections = this.cart.map(item => {
      return {
        product: item.product,
        quantity: item.quantity,
        upc: item.upc,
      } as ProductSelection;
    });

    const data = {
      state: this.orderInformation.state,
      productSelections: selections
    };

    const [err, result] = await to(this.httpClient.post('/.netlify/functions/calculate-taxes', data, { observe: 'response' }).toPromise());

    if (!!err && !!result && result.status !== 200) {
      const body = result.body as any;
      if (!!body) {
        console.error('error calculating taxes', body);
        this.error = body.message;
        return null;
      }

      this.error = 'Unknown error calculating taxes';
      return false;
    } else if (err) {
      this.error = 'Unknown error calculating taxes';
      return null;
    }

    const taxes = result.body['taxes'];
    const message = result.body['message'];

    this.salesTax = taxes;
    this.salesTaxMessage = message;
  }

  removeItem(item: CartItem) {
    this.sandbox.removeItemFromCart(item);
  }

  async changeQuantity(item: CartItem, quantity: number) {
  //console.log('quantity now', quantity);
    this.sandbox.updateItemInCart(item, quantity);

    await this.calculateAll();
  }


  getSubTotal() {
    let total = 0;
    this.cart.forEach(o => {
      total += ((o.product.price * 100) * o.quantity) / 100;
    });
    return total;
  }

  getTotal() {
    let total = this.getSubTotal();
    total += !!this.selectedShippingRate ? parseFloat(this.selectedShippingRate.amount) : 0;
    total += !!this.salesTax ? parseFloat(this.salesTax) : 0;
    return total;
  }

  complete() {
  //console.log('clicked complete');

    // FIXME - complete the order here
    this.completed.emit({
      cart: this.cart,
      shippingRate: this.selectedShippingRate,
      subtotal: this.getSubTotal(),
      total: this.getTotal(),
    });

    // this.errors.emit(new Error('Some Error'));
  }

  async calculateAll() {
    this.error = null;
    await this.calculateShipping();
    await this.calculateSalesTax();
  }

  // trackBySku(index: number, item: CartItem) {
  //   return 'sku' + item.product.id;
  // }

  safeStyle(style: string): SafeStyle {
    return this.sanitize.bypassSecurityTrustStyle(style);
  }

}
